import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useCallback, useState } from 'react';
import { EmptySimpleDate, makeMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { HR } from 'foundations-components/transitional/components/HR';
import { Text } from 'foundations-components/components/Text';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { AbstractProgress } from 'foundations-components/transitional/components/AbstractProgress';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled, { useTheme } from 'styled-components';
import { useSubscription } from '../../../hooks/useSubscription';
import { isNone } from '../../../utils/common';
import { EMPTY_VALUE, TEST_IDS, URLS } from '../../../utils/constants';
import { ONE_WEEK_IN_MILLISECONDS, createSimpleDate, getDateIntervalDifference } from '../../../utils/dateTime';
import { handleError } from '../../../utils/errorHandling';
import { useTracker } from '../../../utils/usageTracker';
import { RedAlertBanner } from '../components/RedAlertBanner';
import { useEditNextPaymentDate } from './useEditNextPaymentDate';
import { useEditNextPaymentDateQuery } from './useEditNextPaymentDateQuery';
import { useNotificationsQuery } from '../hooks/useNotificationsQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledTile = styled.div.withConfig({
  displayName: "EditNextPaymentDateModal__StyledTile",
  componentId: "q59bc6-0"
})(({
  theme
}) => `
  border: 1px solid ${theme.color['border-core-subtle']};
  border-radius: 4px;
  padding: 8px 16px;
  margin: 0 0 12px;
  background-color: ${theme.color['fill-surface-recessed']};
`);
export function EditNextPaymentDateModal({
  objectId,
  actions,
  refetchStaleData
}) {
  const theme = useTheme();
  const [isInNoticePeriod, setIsInNoticePeriod] = useState(false);
  const [proposedPaymentDueAt, setProposedPaymentDueAt] = useState(0);
  const isEmptyDate = proposedPaymentDueAt === 0;
  const {
    trackInteraction
  } = useTracker();
  const {
    close
  } = actions;
  const handleClose = useCallback(() => {
    close();
    trackInteraction('updateSubscriptionNextPaymentDateModalClosed');
  }, [close, trackInteraction]);
  const {
    nextPaymentDueDate,
    lastPaymentDate,
    buyer,
    checkoutEmail
  } = useSubscription();
  const doesNotHaveBuyerEmail = isNone(buyer) || !buyer.email;
  const customerEmail = doesNotHaveBuyerEmail ? checkoutEmail : buyer.email;
  const {
    sendEmailForSubscriptionBillingDateChanged
  } = useNotificationsQuery();
  const {
    data: queryData,
    loading: queryLoading
  } = useEditNextPaymentDateQuery(objectId);
  const {
    submitUpdateBillingDate,
    submitLoading
  } = useEditNextPaymentDate(objectId, () => {
    refetchStaleData();
    close();
    actions.revalidate().catch(handleError);
  });
  if (queryLoading || !queryData) {
    return /*#__PURE__*/_jsx(Modal, {
      "data-test-id": TEST_IDS.EditNextBillingDate,
      children: /*#__PURE__*/_jsxs(Form, {
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h2",
            paddingBlock: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.EditNextPaymentDateModal.title"
            })
          }), /*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: handleClose
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(AbstractProgress, {
              Component: NanoProgress,
              maximum: 35
            }), /*#__PURE__*/_jsx(LoadingSpinner, {
              size: "small",
              minHeight: 100,
              layout: "centered",
              grow: true,
              showLabel: true,
              "data-test-id": "edit-next-payment-date-modal:loading",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.EditNextPaymentDateModal.loadingMessage"
              })
            })]
          })
        })]
      })
    });
  }
  const {
    earliestDueAt,
    subscriptionInvoiceId,
    mutable
  } = queryData;
  const onChange = ({
    target: {
      value
    }
  }) => {
    let proposedDate;
    if (value !== null) {
      proposedDate = makeMoment('utc', value).valueOf();
    } else {
      proposedDate = makeMoment('utc', EmptySimpleDate).valueOf();
    }
    const dateIntervalDifference = getDateIntervalDifference(proposedDate, Number(lastPaymentDate));
    if (dateIntervalDifference <= ONE_WEEK_IN_MILLISECONDS) {
      setIsInNoticePeriod(true);
    } else {
      setIsInNoticePeriod(false);
    }
    if (value !== null) {
      setProposedPaymentDueAt(proposedDate);
    } else {
      setProposedPaymentDueAt(0);
    }
  };
  const getDescriptionMessage = () => {
    if (sendEmailForSubscriptionBillingDateChanged) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "subscriptions.EditNextPaymentDateModal.description_jsx",
        options: {
          href: URLS.KBEmailConfirmation,
          email: customerEmail
        },
        elements: {
          Link
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "subscriptions.EditNextPaymentDateModal.descriptionNoNotify"
    });
  };
  return /*#__PURE__*/_jsx(Modal, {
    "data-test-id": TEST_IDS.EditNextBillingDate,
    children: /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h2",
          use: "on-dark",
          paddingBlock: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.title"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: handleClose
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [doesNotHaveBuyerEmail && /*#__PURE__*/_jsx(RedAlertBanner, {
          message: "subscriptions.NoAssociatedContactEmailAlert.modalMessage"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "subscriptions.EditNextPaymentDateModal.description"
        }), /*#__PURE__*/_jsx(HR, {
          marginBlock: "400"
        }), /*#__PURE__*/_jsx(StyledTile, {
          theme: theme,
          children: /*#__PURE__*/_jsxs(Grid, {
            marginBlock: "200",
            children: [/*#__PURE__*/_jsxs(GridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(Text, {
                as: "small",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.EditNextPaymentDateModal.lastBillingDate"
                })
              }), /*#__PURE__*/_jsx(Text, {
                as: "p",
                marginBlockEnd: "0",
                children: !lastPaymentDate ? /*#__PURE__*/_jsx("span", {
                  children: EMPTY_VALUE
                }) : /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(lastPaymentDate),
                  format: "ll"
                })
              })]
            }), /*#__PURE__*/_jsxs(GridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(Text, {
                as: "small",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.EditNextPaymentDateModal.nextBillingDate"
                })
              }), /*#__PURE__*/_jsx(Text, {
                as: "p",
                marginBlockEnd: "0",
                children: /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(nextPaymentDueDate),
                  format: "ll"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.datePicker.title"
          }),
          children: /*#__PURE__*/_jsx(DateInput, {
            "data-test-id": "edit-next-payment-date-picker",
            min: createSimpleDate(earliestDueAt),
            minValidationMessage: I18n.text('subscriptions.EditNextPaymentDateModal.datePicker.minValidationMessage'),
            onChange: onChange
          })
        }), isInNoticePeriod && /*#__PURE__*/_jsx(Text, {
          as: "small",
          color: "text-core-subtle",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.datePicker.noticePeriodWarningMessage"
          })
        }), /*#__PURE__*/_jsx(HR, {
          marginBlockStart: "400",
          marginBlockEnd: "400"
        }), getDescriptionMessage()]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          disabled: !isEmptyDate,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.emptyDateTooltip"
          }),
          placement: "left",
          children: /*#__PURE__*/_jsx(LoadingButton, {
            use: "primary",
            onClick: () => submitUpdateBillingDate({
              nextPaymentDueDate,
              subscriptionInvoiceId: subscriptionInvoiceId,
              proposedPaymentDueAt
            }),
            "data-test-id": `${TEST_IDS.EditNextBillingDate}:submit`,
            loading: submitLoading,
            disabled: isEmptyDate || queryLoading || !mutable,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.EditNextPaymentDateModal.update"
            })
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: handleClose,
          "data-test-id": `${TEST_IDS.EditNextBillingDate}:cancel`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.EditNextPaymentDateModal.cancel"
          })
        })]
      })]
    })
  });
}